import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { FaFileCsv, FaUpload, FaTimes, FaSpinner, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

export const UploadRoutes = ({ onUploadSuccess }) => {
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccessMessage, setUploadSuccessMessage] = useState(null);
    const fileInputRef = useRef(null);
    const dragCounter = useRef(0);

    const authToken = localStorage.getItem('authToken');

    const allowedFileTypes = [
        '.csv',
        '.xls',
        '.xlsx',
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    const validateFile = useCallback((file) => {
        if (!file) return false;
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        const fileType = file.type.toLowerCase();
        if (allowedFileTypes.includes(fileExtension) || allowedFileTypes.includes(fileType)) {
            setError('');
            return true;
        } else {
            setError('Invalid file type. Please upload a CSV, XLS, or XLSX file.');
            return false;
        }
    }, [allowedFileTypes]);

    const handleFile = useCallback((file) => {
        if (validateFile(file)) {
            setUploadedFile(file);
        } else {
            setUploadedFile(null);
        }
    }, [validateFile]);

    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (dragCounter.current === 1) {
            setIsDragging(true);
        }
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        dragCounter.current = 0;
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFile(e.dataTransfer.files[0]);
        }
    }, [handleFile]);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            handleFile(e.target.files[0]);
        }
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const handleClearFile = () => {
        setUploadedFile(null);
        setError('');
        setUploadError(null);
        setUploadSuccessMessage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const handleUpload = async () => {
        if (uploadedFile) {
            setIsUploading(true);
            setUploadError(null);
            setUploadSuccessMessage(null);

            const formData = new FormData();
            formData.append('uploaded_file', uploadedFile);

            try {
                const response = await axios.post(
                    API_ENDPOINTS.uploadRoutes,
                    formData,
                    {
                        headers: {
                            Authorization: `Token ${authToken}`,
                        },
                    }
                );
                const responseData = response.data;

                setIsUploading(false);

                setUploadSuccessMessage(responseData.message || 'File uploaded and processed successfully!');
                if (onUploadSuccess && responseData.data) {
                    onUploadSuccess(responseData.data);
                }
                setUploadedFile(null); // Clear file on success
            } catch (error) {
                console.error("Upload error:", error);
                setUploadError(error.response?.data?.error || error.message || 'An unexpected error occurred.');
            } finally {
                setIsUploading(false);
            }
        } else {
            setUploadError('Please select a file to upload.');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-sm w-full max-w-2xl mx-auto">
            <div className="text-center mb-4">
                <h2 className="text-xl font-nunito font-bold text-gray-800">Upload Routes File</h2>
                <div className="text-sm text-gray-600 mt-2 space-y-1">
                    <p className="font-nunito">Drag and drop your .csv, .xls, or .xlsx file below, or click to browse.</p>
                    <p className="font-nunito">Lists of addresses and stop durations should be separated by a semicolon (;) without a whitespace.</p>
                    <p className="font-nunito">Ex.: Addresses: stop1;stop2;stop3, Stop durations: 60;120</p>
                </div>
                <p className="mt-3 text-sm font-nunito">
                    Download template:
                    <a className="ml-2 font-medium text-blue-600 hover:text-blue-800 font-nunito" href="/templates/route_template.csv" download>.csv</a>,
                    <a className="ml-2 font-medium text-blue-600 hover:text-blue-800 font-nunito" href="/templates/route_template.xlsx" download>.xlsx</a>
                </p>
            </div>

            <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors duration-200 ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={!uploadedFile ? handleBrowseClick : undefined}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".csv, .xls, .xlsx, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    className="hidden"
                />
                {uploadedFile ? (
                    <div className="flex items-center justify-center gap-4 text-gray-700">
                        <FaFileCsv className="text-3xl text-green-600" />
                        <div className="text-left">
                            <p className="font-semibold">{uploadedFile.name}</p>
                            <p className="text-sm text-gray-500">({(uploadedFile.size / 1024).toFixed(2)} KB)</p>
                        </div>
                        <button onClick={handleClearFile} className="p-1 text-gray-500 hover:text-red-600 rounded-full transition-colors">
                            <FaTimes size={20} />
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center text-gray-500">
                        <FaUpload className="text-4xl mb-3" />
                        <p className="font-semibold font-nunito">Drag & drop file here</p>
                        <p className="my-2 text-sm font-nunito">or</p>
                        <button type="button" onClick={handleBrowseClick} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md font-semibold hover:bg-gray-300 transition-colors font-nunito">
                            Browse Files
                        </button>
                    </div>
                )}
            </div>

            {error && <p className="mt-3 text-sm text-red-600 text-center font-nunito">{error}</p>}

            {uploadedFile && !error && (
                <div className="mt-6 text-center">
                    <button onClick={handleUpload} className="w-full btn-primary" disabled={isUploading}>
                        {isUploading ? (
                            <div className="flex items-center justify-center gap-2">
                                <FaSpinner className="animate-spin" />
                                <span>Uploading...</span>
                            </div>
                        ) : 'Process Routes File'}
                    </button>
                </div>
            )}
            
            {uploadError && (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                    <FaExclamationCircle />
                    <span>{uploadError}</span>
                </div>
            )}
            {uploadSuccessMessage && (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-green-700 bg-green-50 p-3 rounded-md">
                    <FaCheckCircle />
                    <span>{uploadSuccessMessage}</span>
                </div>
            )}
        </div>
    );
};

export default UploadRoutes;